import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const slides = [
    {
        id: "atelier",
        url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_silver-studio-new/artifacts/q53qyrjs_silver_Cinematic_photorealistic_hero_image_for_a_boutique_sol_33517b0c-bab3-4398-baa4-49db1e6a0bc5_3.png",
        label: "Atelier",
    },
    {
        id: "studio",
        url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_silver-studio-new/artifacts/dhrlbpvq_silver_a_high_angle_shot_of_a_cluttered_study_desk._a_stack_o_197e32e4-a5d9-4e1b-8ad8-a88da85999ee_3.png",
        label: "Studio",
    },
    {
        id: "eclipse",
        url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_silver-studio-new/artifacts/egvaslaz_silver_Cinematic_still_life_of_a_modern_design_studio_at_dusk_eee3fef8-dd84-43f5-b392-f189cf17177d_0.png",
        label: "Eclipse",
    },
];

const Hero = () => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), 8000);
        return () => clearInterval(t);
    }, []);

    const goTo = (i) => setIndex(i);

    return (
        <section
            data-testid="hero-section"
            className="relative h-screen min-h-[680px] w-full overflow-hidden flex items-end bg-[#0a0a0a]"
        >
            {/* Crossfade slider — plain CSS opacity transitions, both images preloaded */}
            {slides.map((s, i) => (
                <div
                    key={s.id}
                    data-testid={`hero-slide-${s.id}`}
                    className="absolute inset-0 z-0"
                    style={{
                        opacity: i === index ? 1 : 0,
                        transition: "opacity 1400ms ease-in-out",
                    }}
                >
                    <img
                        src={s.url}
                        alt={`${s.label} — SilverTree Design Studio`}
                        className="w-full h-full object-cover"
                        loading="eager"
                        style={{
                            transform: i === index ? "scale(1.06)" : "scale(1.0)",
                            transition: "transform 9000ms linear",
                        }}
                    />
                </div>
            ))}

            <div className="hero-video-overlay" />

            {/* Top right corner */}
            <div className="absolute top-28 right-6 lg:right-12 z-10 text-right text-[var(--st-cream)]">
                <p className="eyebrow opacity-80">Gurgaon · Kuala Lumpur</p>
                <p className="font-serif italic text-2xl mt-1">est. 2010</p>
            </div>

            {/* Bottom content */}
            <div className="relative z-10 st-container w-full pb-16 lg:pb-24 text-[var(--st-cream)]">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="eyebrow mb-6 text-[var(--st-gold)]"
                    data-testid="hero-eyebrow"
                >
                    A boutique brand & design studio · 15+ years · 50+ brands
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.15 }}
                    data-testid="hero-title"
                    className="font-serif leading-[0.92] tracking-tighter"
                    style={{ fontSize: "clamp(3.2rem, 8.5vw, 8.5rem)" }}
                >
                    Design.<br />
                    Consult.<br />
                    <span className="italic text-[var(--st-gold)]">Learn.</span>
                </motion.h1>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.4 }}
                    className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:items-end"
                >
                    <p className="lg:col-span-5 text-base md:text-lg leading-relaxed text-[#e8e5d4] max-w-md">
                        SilverTree builds the brands founders want to be remembered by.
                        We design, we strategise, we teach — sometimes all on the same project.
                    </p>

                    <div className="lg:col-span-5 lg:col-start-8 flex flex-col sm:flex-row gap-3 lg:justify-end">
                        <Link
                            to="/work"
                            data-testid="hero-cta-explore"
                            className="btn-primary bg-transparent border-[var(--st-cream)] text-[var(--st-cream)] backdrop-blur-sm hover:bg-[var(--st-cream)] hover:text-[var(--st-ink)]"
                            style={{ backgroundColor: "rgba(15, 15, 15, 0.35)" }}
                        >
                            Explore our work <ArrowUpRight size={16} />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="hero-cta-book"
                            className="btn-primary bg-[var(--st-gold)] border-[var(--st-gold)] text-[var(--st-ink)] hover:bg-[var(--st-cream)] hover:border-[var(--st-cream)]"
                        >
                            Book a call <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </motion.div>
            </div>

            {/* Slider pagination */}
            <div className="absolute bottom-6 right-6 lg:right-12 z-20 flex items-center gap-3">
                {slides.map((s, i) => (
                    <button
                        key={s.id}
                        onClick={() => goTo(i)}
                        data-testid={`hero-slide-dot-${s.id}`}
                        aria-label={`Switch to ${s.label} slide`}
                        className="group flex items-center"
                    >
                        <span
                            className={`block h-px transition-all duration-500 ${
                                i === index ? "w-12 bg-[var(--st-gold)]" : "w-6 bg-[#c8c5b3]/50"
                            }`}
                        />
                    </button>
                ))}
            </div>

            {/* Scroll indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.9 }}
                transition={{ delay: 1.2, duration: 1 }}
                className="absolute bottom-6 left-[56%] lg:left-[68%] -translate-x-1/2 z-10 text-[var(--st-cream)] flex flex-col items-center gap-2"
            >
                <ArrowDown size={18} className="animate-bounce" />
                <span className="eyebrow text-[10px] opacity-70">Scroll</span>
            </motion.div>
        </section>
    );
};

export default Hero;
